const WS_TRACKING_PREFIX = 'ind_socket_ws_';
const NATS_TRACKING_PREFIX = 'ind_socket_nats_';
const PROVIDER_TRACKING_PREFIX = 'ind_socket_provider_';

export type WsTrackingEventName = `${typeof WS_TRACKING_PREFIX}${string}`;
export type NatsTrackingEventName = `${typeof NATS_TRACKING_PREFIX}${string}`;
export type ProviderTrackingEventName =
  `${typeof PROVIDER_TRACKING_PREFIX}${string}`;

export const makeWsTrackingEventName = <T extends string>(
  eventName: T,
): WsTrackingEventName => {
  return `${WS_TRACKING_PREFIX}${eventName}`;
};

export const makeNatsTrackingEventName = <T extends string>(
  eventName: T,
): NatsTrackingEventName => {
  return `${NATS_TRACKING_PREFIX}${eventName}`;
};

export const makeProviderTrackingEventName = <T extends string>(
  eventName: T,
): ProviderTrackingEventName => {
  return `${PROVIDER_TRACKING_PREFIX}${eventName}`;
};

/**
 * WebSocket tracking events (prefix: ind_socket_ws_)
 * Use `client` field to distinguish: PRICES | TRADES | US_PRICES
 */
export const WS_TRACKING_EVENTS = {
  // Initialization
  INIT_SOCKET_MANAGER: makeWsTrackingEventName('init_socket_manager'),
  INIT_CONNECTION_START: makeWsTrackingEventName('init_connection_start'),
  INIT_CONNECTION_ALREADY_IN_PROGRESS: makeWsTrackingEventName(
    'init_connection_already_in_progress',
  ),
  INIT_CONNECTION_ERROR: makeWsTrackingEventName('init_connection_error'),
  INITIALIZE_CONNECTION_ERROR: makeWsTrackingEventName(
    'initialize_connection_error',
  ),

  // Connection lifecycle
  CONNECTED: makeWsTrackingEventName('connected'),
  DISCONNECTED: makeWsTrackingEventName('disconnected'),
  ERROR: makeWsTrackingEventName('error'),

  // Message processing
  MESSAGE_NO_SUBPATH: makeWsTrackingEventName('message_no_subpath'),
  MESSAGE_NO_SUBSCRIPTION: makeWsTrackingEventName('message_no_subscription'),
  MESSAGE_CALLBACK_ERROR: makeWsTrackingEventName('message_callback_error'),
  MESSAGE_PROCESSING_ERROR: makeWsTrackingEventName('message_processing_error'),

  // Subscription
  SUBSCRIBE_SUCCESS: makeWsTrackingEventName('subscribe_success'),
  UNSUBSCRIBE_SUCCESS: makeWsTrackingEventName('unsubscribe_success'),
  SUBSCRIBE_NO_CONNECTION: makeWsTrackingEventName('subscribe_no_connection'),
  SUBSCRIBE_ERROR: makeWsTrackingEventName('subscribe_error'),
  UNSUBSCRIBE_ERROR: makeWsTrackingEventName('unsubscribe_error'),

  // Reconnection (base websocket-manager)
  RECONNECT_ON_FOCUS: makeWsTrackingEventName('reconnect_on_focus'),
  RECONNECTION_ON_FOCUS: makeWsTrackingEventName('reconnect_on_focus'),
  RECONNECT_CONNECTION_IN_PROGRESS: makeWsTrackingEventName(
    'reconnect_connection_in_progress',
  ),
  RECONNECT_TIMER_EXIST: makeWsTrackingEventName('reconnect_timer_exist'),
  CONNECTION_EXIST_ON_RECONNECT: makeWsTrackingEventName(
    'connection_exist_on_reconnect',
  ),
  MAX_RECONNECTION_ATTEMPTS: makeWsTrackingEventName(
    'max_reconnection_attempts',
  ),
  RECONNECT_ATTEMPT_TRIGGERED: makeWsTrackingEventName(
    'reconnect_attempt_triggered',
  ),
  RECONNECTION_ATTEMPT: makeWsTrackingEventName('reconnection_attempt'),
  CONNECT_SUCCESS: makeWsTrackingEventName('connect_success'),
  CONNECT_ERROR: makeWsTrackingEventName('connect_error'),
  DISCONNECTION: makeWsTrackingEventName('disconnection'),
  DISCONNECTION_ERROR: makeWsTrackingEventName('disconnection_error'),

  // Consumer hook events (use `hook` field to identify: useLivePrice, useTradeEvents, etc.)
  HOOK_MANAGER_NOT_INITIALIZED: makeWsTrackingEventName(
    'hook_manager_not_initialized',
  ),
  HOOK_CONNECTION_NOT_READY: makeWsTrackingEventName(
    'hook_connection_not_ready',
  ),
  HOOK_SUBSCRIBE_ERROR: makeWsTrackingEventName('hook_subscribe_error'),
  HOOK_SOCKET_PATH_NOT_FOUND: makeWsTrackingEventName(
    'hook_socket_path_not_found',
  ),
} as const;

/**
 * NATS tracking events (prefix: ind_socket_nats_)
 */
export const NATS_EVENT_NAMES = {
  // Library events
  CONNECTED: makeNatsTrackingEventName('connected'),
  DISCONNECTED: makeNatsTrackingEventName('disconnected'),
  RECONNECTED: makeNatsTrackingEventName('reconnected'),
  ERROR: makeNatsTrackingEventName('error'),
  SUBSCRIPTION_SUCCESS: makeNatsTrackingEventName('subscription_success'),
  SUBSCRIPTION_ERROR: makeNatsTrackingEventName('subscription_error'),
  UNSUBSCRIPTION_SUCCESS: makeNatsTrackingEventName('unsubscription_success'),
  CONNECTION_CLOSED: makeNatsTrackingEventName('connection_closed'),
  CONNECTION_STALE: makeNatsTrackingEventName('connection_stale'),
  CONNECTION_CLOSED_ON_FOCUS: makeNatsTrackingEventName(
    'connection_closed_on_focus',
  ),
  CONNECTION_DRAINING_ON_FOCUS: makeNatsTrackingEventName(
    'connection_draining_on_focus',
  ),

  // Consumer hook events (use `hook` field to identify: useLivePrice, useLiveDepth, etc.)
  HOOK_ERROR: makeNatsTrackingEventName('hook_error'),
  HOOK_SUBSCRIBE_ERROR: makeNatsTrackingEventName('hook_subscribe_error'),
} as const;

export const PROVIDER_EVENT_NAMES = {
  INIT_CONNECTIONS_START: makeProviderTrackingEventName(
    'init_connections_start',
  ),
  INIT_CONNECTIONS_CONFIG_MISSING: makeProviderTrackingEventName(
    'init_connections_config_missing',
  ),
  INIT_CONNECTIONS_ERROR: makeProviderTrackingEventName(
    'init_connections_error',
  ),
  // Token changed
  NATS_TOKEN_CHANGED: makeProviderTrackingEventName('nats_token_changed'),
} as const;
